import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Users, FileText, Download, Settings } from "lucide-react";
import { authMe, logout } from "@/api";
import { Button } from "@/components/ui/button";

const TABS = [
  { to: "/admin", label: "Leads", icon: Users, end: true },
  { to: "/admin/content", label: "CMS Content", icon: FileText },
  { to: "/admin/downloads", label: "Downloads", icon: Download },
];

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [state, setState] = useState("checking"); // checking | ok | denied | anon
  const navigate = useNavigate();

  useEffect(() => {
    authMe()
      .then((u) => {
        setUser(u);
        setState(u.is_admin ? "ok" : "denied");
      })
      .catch(() => setState("anon"));
  }, []);

  const onLogout = async () => {
    await logout().catch(() => {});
    navigate("/admin/login");
  };

  if (state === "checking")
    return <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground" data-testid="admin-loading">Checking access…</div>;

  if (state === "anon") {
    navigate("/admin/login", { replace: true });
    return null;
  }

  if (state === "denied")
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background" data-testid="admin-denied">
        <p className="text-2xl text-foreground">Access restricted</p>
        <p className="max-w-sm text-center text-sm text-muted-foreground">
          {user?.email} is not on the admin list for this site. Sign in with an authorised Google account.
        </p>
        <Button variant="outline" className="rounded-sm" onClick={onLogout} data-testid="admin-denied-logout-btn">
          Sign out
        </Button>
      </div>
    );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-semibold text-foreground">TruVerse</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-accent">Admin</span>
          </div>
          <nav className="flex items-center gap-1" aria-label="Admin">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`admin-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-sm px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${
                    isActive ? "bg-accent/15 text-accent" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                <t.icon size={14} /> <span className="hidden sm:inline">{t.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-muted-foreground md:inline" data-testid="admin-user-email">{user?.email}</span>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={onLogout} data-testid="admin-logout-btn">
              <LogOut size={14} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}
